/**
 * Sub-flow region node — the bordered "perimeter" wrapper that holds
 * an inline sub-flow on the unified canvas (ADR-0008 PR3b3b).
 *
 * Visually: a translucent rounded rectangle with a header chip showing
 * the parent branch label (e.g. "↪ sblocca · sub-flow") + a small
 * "Edit standalone" link that opens the sub-flow's own editor page in
 * a new tab (useful for power users who want to run / version / trace
 * the sub-flow in isolation).
 *
 * The actual sub-flow nodes are rendered AS CHILDREN by React Flow
 * (each child has ``parentNode: <regionId>`` + ``extent: "parent"``).
 * The region itself is a React Flow node of type ``subflowRegion`` —
 * registered alongside ``tray`` in WorkflowEditor's NODE_TYPES.
 *
 * Behaviours that matter:
 *   - The region itself is draggable, so the operator can shuffle
 *     it around the canvas. Children move with it (React Flow handles
 *     this automatically when extent="parent").
 *   - No connection handles on the wrapper itself — the flow into the
 *     sub-flow goes through the sink branch handle (drawn by
 *     TrayNodeBox) → the child ``delivery_trigger`` source node inside.
 *   - The wrapper is `zIndex: 0` so children render ABOVE it (and the
 *     header chip uses zIndex: 1 so it stays clickable above children).
 */
import { ExternalLink, Trash2 } from "lucide-react";
import * as React from "react";
import { NodeProps } from "reactflow";


export interface SubflowRegionNodeData {
  /** Workflow id of the linked sub-flow. */
  workflowId: string;
  /** Display label (e.g. "sblocca" or "Sblocca documento"). */
  label: string;
  /** Branch id the region is wired to on the parent sink. Shown for
   *  audit context in the header chip. */
  parentBranchId: string;
  /** True while we're still loading the sub-flow's active version
   *  definition — children won't be visible until then. */
  isLoading?: boolean;
  /** True when the linked workflow has no active version (operator
   *  disabled it). Surfaced as a small warning chip. */
  hasNoActiveVersion?: boolean;
  /** Fired when the operator clicks the trash icon on the wrapper.
   *  The handler unlinks the branch + DELETEs the sub-flow workflow
   *  + auto-saves the parent. */
  onDelete?: () => void;
}


export function SubflowRegionNode({
  data,
}: NodeProps<SubflowRegionNodeData>) {
  const openStandalone = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`/workflows/${data.workflowId}`, "_blank");
  };

  return (
    <div
      className="relative h-full w-full rounded-lg border-2 border-dashed border-emerald-500/40 bg-emerald-500/[0.04]"
      // Wrapper deliberately doesn't react to clicks on its own body —
      // children handle their own interactions. ReactFlow's drag
      // mechanism is wired via the node header / border.
    >
      {/* Header chip — sticks to the top-left of the region. Mirrors
          the visual language of the SinkBranch handle chip on the
          parent sink so the operator can mentally connect the two. */}
      <div
        className="absolute -top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded border border-emerald-400/40 bg-elevated px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300"
        onClick={(e) => e.stopPropagation()}
      >
        <span>↪ {data.parentBranchId}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-foreground">{data.label}</span>
        {data.isLoading && (
          <span className="text-muted-foreground">loading…</span>
        )}
        {data.hasNoActiveVersion && (
          <span
            className="text-amber-300"
            title="The linked workflow has no active version"
          >
            ⚠ no active version
          </span>
        )}
        <button
          type="button"
          title="Open this sub-flow in a standalone editor (new tab)"
          onClick={openStandalone}
          className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground"
        >
          <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
        </button>
        {/* Delete the whole sub-flow: unlinks the branch on the parent
            + DELETEs the hidden sub-flow workflow + auto-saves. The
            child trigger node + the virtual link edge are non-deletable
            on their own — getting rid of a sub-flow goes through this
            button so the contract stays atomic. */}
        {data.onDelete && (
          <button
            type="button"
            title="Remove this sub-flow (unlinks the branch + deletes the workflow)"
            onClick={(e) => {
              e.stopPropagation();
              if (
                !window.confirm(
                  `Remove the "${data.label}" sub-flow? The branch will be unlinked and the sub-flow workflow will be deleted. This cannot be undone.`,
                )
              ) {
                return;
              }
              data.onDelete?.();
            }}
            className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-danger"
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}
